import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Input } from '../components/ui/input';
import {
  Coins, Send, ArrowUpRight, ArrowDownLeft, ShoppingBag,
  History, Wallet, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import BuyRunegoldModal from '../components/economy/BuyRunegoldModal';

export default function WalletPage() {
  const { user, updateUser } = useAuth();
  const [walletData, setWalletData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [transferTo, setTransferTo] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showBuyModal, setShowBuyModal] = useState(false);

  const fetchWallet = useCallback(async () => {
    try {
      const [w, t] = await Promise.all([
        api.get('/wallet'),
        api.get('/wallet/transactions'),
      ]);
      setWalletData(w.data);
      setTransactions(t.data || []);
      if (w.data?.balance !== undefined) {
        updateUser({ wonder_coins: w.data.balance });
      }
    } catch {
      toast.error('Failed to load wallet');
    } finally {
      setLoading(false);
    }
  }, [updateUser]);

  useEffect(() => { fetchWallet(); }, [fetchWallet]);

  const handleTransfer = async () => {
    const amt = parseInt(transferAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (!transferTo.trim()) { toast.error('Enter a recipient username or ID'); return; }
    setSending(true);
    try {
      const res = await api.post('/wallet/transfer', {
        recipient_id: transferTo.trim(),
        amount: amt,
      });
      toast.success(`Sent ${amt} Runegold!`);
      updateUser({ wonder_coins: res.data.new_balance });
      setWalletData((prev) => prev ? { ...prev, balance: res.data.new_balance } : prev);
      setTransferTo('');
      setTransferAmount('');
      fetchWallet();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Transfer failed');
    } finally {
      setSending(false);
    }
  };

  const balance = walletData?.balance ?? user?.wonder_coins ?? 0;

  if (loading) {
    return (
      <div className="px-4 py-8 flex justify-center">
        <div className="animate-pulse space-y-6 w-full max-w-2xl">
          <div className="h-40 rounded-xl bg-muted" />
          <div className="h-32 rounded-lg bg-muted" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6" data-testid="wallet-page">
      <div className="max-w-2xl mx-auto space-y-5">

        {/* Balance Card */}
        <div className="mg-card-elevated p-6 text-center border-[hsl(var(--mg-gold-deep)/0.25)]" data-testid="wallet-balance-card">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Wallet className="w-5 h-5 text-[hsl(var(--mg-gold))]" />
            <h1 className="font-heading text-base font-bold">Runegold Wallet</h1>
          </div>
          <div className="flex items-center justify-center gap-2 my-4">
            <Coins className="w-8 h-8 text-[hsl(var(--mg-gold))] mg-gold-glow" />
            <span className="text-4xl font-black tracking-tight mg-gold-text" data-testid="wallet-balance">{balance}</span>
          </div>
          <p className="text-xs text-muted-foreground mb-4 uppercase tracking-wider">Runegold Balance</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => setShowBuyModal(true)}
              className="mg-btn mg-btn-gold text-sm px-6 py-2.5"
              data-testid="buy-runegold-btn"
            >
              <ShoppingBag className="w-4 h-4" /> Buy Runegold
            </button>
            <button
              onClick={fetchWallet}
              className="mg-btn mg-btn-secondary text-sm px-5 py-2.5"
              data-testid="refresh-wallet-btn"
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>
        </div>

        {/* Send Runegold */}
        <div className="mg-card-elevated p-5">
          <h2 className="font-heading font-bold text-sm flex items-center gap-2 mb-4">
            <Send className="w-4 h-4 text-[hsl(var(--mg-purple))]" /> Send Runegold
          </h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              placeholder="Username or ID"
              value={transferTo}
              onChange={(e) => setTransferTo(e.target.value)}
              className="mg-input flex-1"
              data-testid="transfer-to-input"
            />
            <div className="relative flex-1">
              <Coins className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--mg-gold))]" />
              <Input
                type="number"
                placeholder="Amount"
                value={transferAmount}
                onChange={(e) => setTransferAmount(e.target.value)}
                className="mg-input pl-9"
                min="1"
                data-testid="transfer-amount-input"
              />
            </div>
            <button onClick={handleTransfer} disabled={sending} className="mg-btn mg-btn-primary text-sm px-5 py-2.5" data-testid="transfer-send-btn">
              {sending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>

        {/* Transaction History */}
        <div className="mg-card-elevated p-5">
          <h2 className="font-heading font-bold text-sm flex items-center gap-2 mb-4">
            <History className="w-4 h-4 text-[hsl(var(--mg-purple))]" /> Transaction History
          </h2>
          {transactions.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No transactions yet. Start by tipping a creator!
            </p>
          ) : (
            <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
              {transactions.map((tx) => {
                const isIncoming = tx.type === 'tip_received' || tx.type === 'reward' || tx.type === 'purchase';
                return (
                  <div key={tx.id} className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-muted/20 transition-colors" data-testid="wallet-transaction">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isIncoming ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                        {isIncoming ? <ArrowDownLeft className="w-4 h-4 text-green-500" /> : <ArrowUpRight className="w-4 h-4 text-red-500" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium capitalize">{tx.type?.replace(/_/g, ' ')}</p>
                        <p className="text-[11px] text-muted-foreground">{new Date(tx.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <span className={`font-bold text-sm ${isIncoming ? 'text-green-500' : 'text-red-500'}`}>
                      {isIncoming ? '+' : '-'}{Math.abs(tx.amount)} <Coins className="w-3 h-3 inline text-[hsl(var(--mg-gold))]" />
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <BuyRunegoldModal open={showBuyModal} onOpenChange={setShowBuyModal} />
    </div>
  );
}
